import type {
  GameState,
  OneNightAction,
} from "@cloudnest/one-night-ultimate-werewolf-engine";

export type OneNightReplayEvent =
  | {
      readonly eventType: "init";
      readonly userId: string;
      readonly payload: { readonly state: GameState };
    }
  | {
      readonly eventType: "SyncViewerState";
      readonly userId: string;
      readonly payload?: { readonly actionId?: string };
    }
  | {
      readonly eventType: "action";
      readonly userId: string;
      readonly payload: {
        readonly schemaVersion: 1;
        readonly action: OneNightAction;
      };
    };

export type OneNightReplayEventType = OneNightReplayEvent["eventType"];

export interface OneNightEventInput {
  readonly eventType: string;
  readonly userId?: string;
  readonly payload?: Record<string, unknown>;
}

export const ONE_NIGHT_ULTIMATE_WEREWOLF_REPLAY_EVENT_TYPES = [
  "init",
  "SyncViewerState",
  "action",
] as const satisfies readonly OneNightReplayEventType[];

function requireUserId(event: OneNightEventInput): string {
  if (typeof event.userId === "string" && event.userId.length > 0) {
    return event.userId;
  }
  throw new Error("One Night replay event is missing userId");
}

export function normalizeOneNightReplayEventType(
  eventType: string,
): OneNightReplayEventType {
  switch (eventType) {
    case "init":
    case "SyncViewerState":
    case "action":
      return eventType;
    default:
      throw new Error(`Unsupported One Night event type: ${eventType}`);
  }
}

export function toOneNightReplayEvent(
  event: OneNightEventInput,
): OneNightReplayEvent {
  const eventType = normalizeOneNightReplayEventType(event.eventType);
  const userId = requireUserId(event);
  const payload = event.payload ?? {};

  switch (eventType) {
    case "init": {
      const state = (payload as { state?: GameState }).state;
      if (!state || typeof state !== "object") {
        throw new Error("One Night init event is missing payload.state");
      }
      return { eventType, userId, payload: { state } };
    }
    case "SyncViewerState":
      return {
        eventType,
        userId,
        payload: payload as { readonly actionId?: string },
      };
    case "action": {
      const typed = payload as {
        schemaVersion?: number;
        action?: OneNightAction;
      };
      if (typed.schemaVersion !== 1 || !typed.action || !typed.action.type) {
        throw new Error("One Night action event has an invalid payload");
      }
      return {
        eventType,
        userId,
        payload: { schemaVersion: 1, action: typed.action },
      };
    }
  }
}
